import { Box, Paper, Stack, Typography, Button } from '@mui/material';
import { Cookie, Lock, Shield } from '@mui/icons-material';
import { alpha } from '@mui/system';

interface CookieBlockerProps {
	category: 'functional' | 'analytics' | 'marketing';
	title?: string;
	description?: string;
	onAccept?: () => void;
}

export const CookieBlocker: React.FC<CookieBlockerProps> = ({
	category,
	title = 'Inhalt blockiert',
	description,
	onAccept
}) => {
	const getCategoryInfo = () => {
		switch (category) {
			case 'functional':
				return {
					label: 'Funktionale Cookies',
					desc: 'Dieser Inhalt benötigt funktionale Cookies für die Anzeige.',
					color: '#2196f3'
				};
			case 'analytics':
				return {
					label: 'Analyse Cookies',
					desc: 'Dieser Inhalt benötigt Analyse-Cookies für die Anzeige.',
					color: '#9c27b0'
				};
			case 'marketing':
				return {
					label: 'Marketing Cookies',
					desc: 'Dieser Inhalt benötigt Marketing-Cookies für die Anzeige.',
					color: '#ff9800'
				};
		}
	};

	const info = getCategoryInfo();

	return (
		<Box
			sx={{
				width: '100%',
				height: '100%',
				minHeight: 300,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: (theme) =>
					`linear-gradient(135deg, ${alpha(info.color, 0.05)} 0%, ${alpha(
						info.color,
						0.02
					)} 100%)`,
				position: 'relative',
				overflow: 'hidden',
				'&::before': {
					content: '""',
					position: 'absolute',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					background: (theme) =>
						`repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              ${alpha(theme.palette.divider, 0.03)} 10px,
              ${alpha(theme.palette.divider, 0.03)} 20px
            )`
				}
			}}
		>
			<Paper
				elevation={0}
				sx={{
					maxWidth: 500,
					mx: 3,
					p: 1,
					textAlign: 'center',
					position: 'relative',
					zIndex: 1,
					background: (theme) =>
						theme.palette.mode === 'dark'
							? alpha(theme.palette.background.paper, 0.8)
							: alpha(theme.palette.background.paper, 0.95),
					backdropFilter: 'blur(10px)',
					border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
					borderRadius: 3
				}}
			>
				<Stack spacing={3} alignItems="center">
					<Box
						sx={{
							position: 'relative',
							width: 80,
							height: 80,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
					>
						<Box
							sx={{
								position: 'absolute',
								width: '100%',
								height: '100%',
								borderRadius: '50%',
								background: `linear-gradient(135deg, ${alpha(
									info.color,
									0.2
								)} 0%, ${alpha(info.color, 0.05)} 100%)`,
								animation: 'pulse 2s ease-in-out infinite',
								'@keyframes pulse': {
									'0%, 100%': { transform: 'scale(1)', opacity: 1 },
									'50%': { transform: 'scale(1.1)', opacity: 0.8 }
								}
							}}
						/>
						<Lock sx={{ fontSize: 40, color: info.color, position: 'relative', zIndex: 1 }} />
					</Box>

					<Stack spacing={1.5}>
						<Typography variant="h6" fontWeight={600}>
							{title}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
							{description || info.desc}
						</Typography>
					</Stack>

					<Box
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 1,
							px: 2,
							py: 0.75,
							borderRadius: 2,
							background: alpha(info.color, 0.1),
							border: `1px solid ${alpha(info.color, 0.2)}`
						}}
					>
						<Shield sx={{ fontSize: 16, color: info.color }} />
						<Typography variant="caption" fontWeight={500} sx={{ color: info.color }}>
							{info.label} erforderlich
						</Typography>
					</Box>

					<Button
						variant="contained"
						startIcon={<Cookie />}
						onClick={onAccept}
						sx={{
							mt: 1,
							background: `linear-gradient(135deg, ${info.color} 0%, ${alpha(
								info.color,
								0.8
							)} 100%)`,
							boxShadow: `0 4px 14px ${alpha(info.color, 0.3)}`,
							'&:hover': {
								background: `linear-gradient(135deg, ${alpha(
									info.color,
									0.9
								)} 0%, ${alpha(info.color, 0.7)} 100%)`,
								boxShadow: `0 6px 20px ${alpha(info.color, 0.4)}`
							}
						}}
					>
						erforderliche Cookies akzeptieren
					</Button>

					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.7 }}
					>
						<Lock sx={{ fontSize: 12 }} />
						Ihre Privatsphäre ist uns wichtig
					</Typography>
				</Stack>
			</Paper>
		</Box>
	);
};